import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { CaretDownOutlined } from "@ant-design/icons";
import { Select as AntSelect } from "antd";
import { Close as CloseIcon, Create as CreateIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  checkPermissions,
  dateParser,
  dateParserWithoutTime,
} from "../../helper";
import { t } from "i18next";
import { getSingleOffset } from "../../services/offsetService";
import { getEmissionCurrencyService } from "../../services/emissionService";
import OffsetsVersionHistory from "./OffsetsVersionHistory";
import FileAttachments from "../common/FileAttachments";
import FileAttachmentsDetails from "../common/FileAttachmentsDetails";

const ProjectsAndOffsetsDetailedView = (props: any) => {
  const permissions = useSelector((state: any) => state.auth.userPermissions);
  const {
    offsetData,
    onClose,
    controller,
    total_count,
    currentIndex,
    navigateNext,
    handleStatusChange,
    detailedSelectRefresh,
  } = props;
  const { Option } = AntSelect;
  const { page, limit } = controller;
  let index = limit * page + currentIndex;
  const navigate = useNavigate();
  const [offsetDetailedItem, setOffsetDetailedItem] = useState<any>({});
  const [versionHistoryData, setVersionHistoryData] = useState([] as any[]);
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [offsetAttachmentData, setOffsetLogAttachmentData] = useState({
    id: "",
    attachments: [],
  } as any);
  const [statusOptions, setStatusOptions] = useState<any>({
    Hold: ["Retire-Partially", "Retire-Fully", "Transfer"],
    "Retired-Partially": ["Retire-Partially", "Retire-Fully", "Transfer"],
  });

  const onNavigateNext = (increment: any) => {
    if (increment > 0 && currentIndex === limit - 1) {
      navigateNext(increment, 1);
    } else if (increment < 0 && currentIndex === 0) {
      navigateNext(increment, -1);
    } else {
      navigateNext(increment, 0);
    }
  };

  useEffect(() => {
    getSingleOffset({ id: offsetData.id })
      .then((res: any) => {
        let offsetDetailedItem = res.data.offset;
        setOffsetDetailedItem(offsetDetailedItem);
        setVersionHistoryData(res.data.transactions);
        if (res.data.offset.attachments) {
          let attachments = JSON.parse(res.data.offset.attachments);
          setOffsetLogAttachmentData({
            attachments: attachments.files,
            id: offsetData.id,
          });
        }
        getEmissionCurrencyService().then((res: any) => {
          let allCurrencies = res.data;
          const currencySymbol = allCurrencies.find(
            (c: any) => c.id === offsetDetailedItem?.currency
          )?.symbol;
          setCurrencySymbol(currencySymbol);
        });
      })
      .catch((err) => {
        console.log(err);
      });
  }, [index, detailedSelectRefresh]);

  const openEdit = () => {
    navigate("/offset/" + offsetData.id, {
      state: {
        offsetDetailedItem: offsetDetailedItem,
        offsetId: offsetData.id,
      },
    });
  };

  return (
    <div className="flex w-70 flex-col px-4 grow pw-detailed-view-main">
      <div className="flex flex-row items-center gap-6 p-2 px-4">
        <div className="flex flex-row items-center">
          <div className="w-[7rem]">Credit Status:</div>
          <div>
            {checkPermissions(["UPDATE_OFFSET_STATUS"], permissions) &&
            (offsetData.credit_status === "Hold" ||
              offsetData.credit_status === "Retired-Partially") ? (
              <AntSelect
                placeholder="Please select"
                dropdownStyle={{
                  minWidth: "12rem",
                  paddingRight: "12px",
                }}
                suffixIcon={
                  <CaretDownOutlined className="!pointer-events-none" />
                }
                value={offsetData?.credit_status}
                className={`pw-offset-status-selection pw-${offsetData.credit_status.toLowerCase()}`}
                onChange={(event: any) => {
                  handleStatusChange(
                    event,
                    offsetDetailedItem,
                    currentIndex,
                    true
                  );
                }}
              >
                {statusOptions[offsetData.credit_status]?.map(
                  (name: any, index: number) => {
                    return (
                      <Option
                        value={name}
                        className={` pw-margin-10 font-bold pw-${name.toLowerCase()}`}
                      >
                        {name}
                      </Option>
                    );
                  }
                )}
              </AntSelect>
            ) : (
              <div
                className={`pw-${offsetData.credit_status.toLowerCase()} pw-offset-status-detail ${
                  offsetData.credit_status === "Retired-Partially" &&
                  "pw-offset-retired-status"
                }`}
              >
                {offsetData.credit_status}
              </div>
            )}
          </div>
        </div>
        <div className="pw-bunit-detailed-header w-full flex justify-end gap-3 pr-2">
          {offsetData.credit_status === "Hold" &&
            checkPermissions(["CREATE_OFFSET"], permissions) && (
              <Button
                onClick={() => openEdit()}
                className="pw-editbox-emission"
              >
                <CreateIcon className="pw-bunit-edit-icon text-neutral-600" />
                <div className="pw-edit">Edit</div>
              </Button>
            )}

          {currentIndex > 0 && (
            <div
              onClick={() => onNavigateNext(-1)}
              className="pw-navigate-next pw-padding-5"
            >
              <img src="assets/icons/prev.svg" alt=" " />
            </div>
          )}
          {currentIndex < total_count - 1 && (
            <div
              onClick={() => onNavigateNext(1)}
              className="pw-navigate-next pw-padding-5 pw-bunit-next"
            >
              <img src="assets/icons/next.svg" alt=" " />
            </div>
          )}

          <div
            onClick={() => {
              onClose();
            }}
            className="pw-navigate-next items-center cursor-pointer"
          >
            <CloseIcon className=" items-center text-neutral-600" />
          </div>
        </div>
      </div>

      <div className="w-full p-6 my-4 bg-white rounded-lg shadow flex-col justify-start items-start gap-6 inline-flex">
        <div className="self-stretch justify-start items-start gap-2 inline-flex">
          <div className="flex-col justify-start items-start gap-6 inline-flex">
            <div className="flex-col justify-start items-start gap-6 flex">
              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Project Id")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.project_id
                    ? offsetDetailedItem.project_id
                    : "-"}
                </div>
              </div>
              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon project name")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.project_name
                    ? offsetDetailedItem.project_name
                    : "-"}
                </div>
              </div>
              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset project type")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.project_type
                    ? offsetDetailedItem.project_type
                    : "-"}
                </div>
              </div>
              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset credit price")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {currencySymbol}{" "}
                  {offsetDetailedItem.carbon_price
                    ? offsetDetailedItem.carbon_price
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t(
                    "Total offset amount (" + offsetDetailedItem.uom_name + ")"
                  )}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.value ? offsetDetailedItem.value : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t(
                    "Available offset amount (" +
                      offsetDetailedItem.uom_name +
                      ")"
                  )}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetData.active_value}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Certificate number")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.certificate_number
                    ? offsetDetailedItem.certificate_number
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon certificate type")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.certificate_type
                    ? offsetDetailedItem.certificate_type
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Registry account id")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.registry_account_id
                    ? offsetDetailedItem.registry_account_id
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Registry owner name")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.registry_owner_name
                    ? offsetDetailedItem.registry_owner_name
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset program")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.program
                    ? offsetDetailedItem.program
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset project developers")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.project_developer
                    ? offsetDetailedItem.project_developer
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset project financed by investors")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.project_financer
                    ? offsetDetailedItem.project_financer
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset project independent verifier")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.independent_verifier
                    ? offsetDetailedItem.independent_verifier
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset credit purchased date")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.purchase_date
                    ? dateParserWithoutTime(offsetDetailedItem.purchase_date)
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Vintage")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.vintage
                    ? offsetDetailedItem.vintage
                    : "-"}
                </div>
              </div>

              <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Monitoring period")}
                </div>
                <div className="text-neutral-900 text-sm">
                  {offsetDetailedItem.monitoring_start_date &&
                  offsetDetailedItem.monitoring_end_date
                    ? dateParserWithoutTime(
                        offsetDetailedItem.monitoring_start_date
                      ) +
                      " - " +
                      dateParserWithoutTime(
                        offsetDetailedItem.monitoring_end_date
                      )
                    : "-"}
                </div>
              </div>

              {/* <div className="flex flex-row gap-6">
                <div className=" w-48 text-slate-500 text-sm font-normal">
                  {t("Carbon offset credit status")}
                </div>
                <div className="text-green-400 text-sm">
                  {offsetDetailedItem.credit_status
                    ? offsetDetailedItem.credit_status
                    : "-"}
                </div>
              </div> */}

              {/* <div className="flex flex-row gap-6">
                    <div className=" w-48 text-slate-500 text-sm font-normal">
                      {t("Status")}
                    </div>
                    <div
                      className={`${
                        !offsetDetailedItem.disabled
                          ? "text-green-400 text-sm font-bold"
                          : "text-neutral-400 text-sm font-bold"
                      }`}
                    >
                      {offsetDetailedItem.disabled ? "InActive" : "Active"}{" "}
                    </div>
                  </div> */}
            </div>
          </div>
        </div>
      </div>
      {offsetAttachmentData?.attachments?.length ? (
        <FileAttachmentsDetails
          emissionAttachmentData={offsetAttachmentData}
          setEmissionAttachmentData={setOffsetLogAttachmentData}
          fromEmissionDetail={true}
          emissionStatus={offsetDetailedItem?.credit_status}
        />
      ) : (
        ""
      )}
      {versionHistoryData.length > 0 && (
        <OffsetsVersionHistory versionHistoryData={versionHistoryData} />
      )}
    </div>
  );
};

export default ProjectsAndOffsetsDetailedView;
